import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

export default function EquipmentCategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const toast = useToast();

  useEffect(() => {
    loadData();
  }, [showInactive]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentService.getCategories(showInactive);
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (category = null) => {
    setEditingCategory(category);
    setForm(category ? { 
      name: category.name, 
      description: category.description || '', 
      isActive: category.isActive 
    } : { name: '', description: '', isActive: true });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setForm({ name: '', description: '', isActive: true });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCategory) {
        await equipmentService.updateCategory(editingCategory.id, form);
        toast.success('Category updated');
      } else {
        await equipmentService.createCategory(form);
        toast.success('Category created');
      }
      closeDialog();
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonBlock lines={5} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Equipment Categories</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="Show inactive"
          />
          <IconButton onClick={loadData} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
            Add Category
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Subcategories</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat) => (
              <TableRow key={cat.id}>
                <TableCell><strong>{cat.name}</strong></TableCell>
                <TableCell>{cat.description || '-'}</TableCell>
                <TableCell>{cat.subcategoryCount}</TableCell>
                <TableCell>
                  <Chip 
                    label={cat.isActive ? 'Active' : 'Inactive'} 
                    color={cat.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openDialog(cat)}><EditIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth multiline rows={2} label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

